import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function ClientDialog({ open, onOpenChange, client, onSaved }) {
  const editing = !!client;
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: client?.name || "",
        email: client?.email || "",
        phone: client?.phone || "",
        password: "",
      });
    }
  }, [open, client]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/clients/${client.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/admin/clients", form);
        toast.success("Client created");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light tracking-tighter">
            {editing ? "Edit client" : "New client"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Full name</Label>
            <Input data-testid="client-name-input" value={form.name} onChange={set("name")} required className="bg-input border-border h-11 focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Email</Label>
            <Input data-testid="client-email-input" type="email" value={form.email} onChange={set("email")} required className="bg-input border-border h-11 focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">Phone (optional)</Label>
            <Input data-testid="client-phone-input" value={form.phone} onChange={set("phone")} className="bg-input border-border h-11 focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">
              {editing ? "New password (leave blank to keep)" : "Password"}
            </Label>
            <Input
              data-testid="client-password-input"
              type="text"
              value={form.password}
              onChange={set("password")}
              required={!editing}
              minLength={editing ? undefined : 6}
              placeholder={editing ? "••••••••" : "Set a login password"}
              className="bg-input border-border h-11 focus-visible:ring-primary"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="submit"
              data-testid="client-save-btn"
              disabled={loading}
              className="rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform gold-glow"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
